
import { CheckboxGroup } from "@/components/atoms/checkbox-group";
import {
  datePickerFormatJP,
  DEFAULT_ADULTS,
  DEFAULT_CHECKIN_DATE,
  DEFAULT_CHECKOUT_DATE,
} from "@/constants";
import { TRIPLA_BOOKING_URL } from "@/constants/enviroment";
import { useModal } from "@/hooks/useModal/useModal";
import { LOCALE_API_MAP, LOCALE_BASE_MAP } from "@/i18n/routing";
import useJapanAreasStore from "@/store/hooks/useGlobal";
import useSearchFormStore from "@/store/searchFormStore/searchFormStore";
import { ERoomOrderBy, ERoomType } from "@/types/roomsList";
import {
  cityAndHotelFuzzyFilter,
  formatDateForStorage,
  formatDateToYYYYMMDD,
  HOT_SPRING_AMENITY,
  checkAndValidateStoredDates,
  redirectBookingCard,
  SelectOption as UtilSelectOption,
} from "@/utils";
import { HotelBrandItemNullable } from "@app/[locale]/brands/[brand_slug]/page";
import { valibotResolver } from "@hookform/resolvers/valibot";
import { Label } from "@radix-ui/react-label";
import { format } from "date-fns";
import { ArrowRight } from "lucide-react";
import * as React from "react";
import { useMemo, useState } from "react";
import { DateRange } from "react-day-picker";
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import * as v from "valibot";
import { TypeSearch } from "../hotel-result";
import { RoomSelect } from "./RoomSelect";
import {
  CustomSearchAvailableProps,
  schemaSearchAvailableRoom,
} from "./SearchAvailableRoomMobile";
import { createBookingParams, deepCloneRoom, TRoom } from "./ultils";
import { useGetMapTextDatasourceQuery } from "../top-page/map-search-section";
import { SetOfTag } from "@/types/generated";
import { IHotelListSliderProps } from "../hotel-list-slider";
import { IHotelOrCity } from "@/types/availableRoom";
import { CustomSelect } from "@/components/atoms/dropdown";
import { Icon } from "@/components/atoms/icon";
import { DateRangePicker } from "@/components/atoms/date-range-picker";
import { Button } from "@/components/atoms/button";
import { Input } from "@/components/atoms/input/Input";
import { Modal } from "@/components/atoms/modal";
import { Loading } from "@/components/atoms/loading";
import { findOption } from "./findOption";
import { useValidatedDates } from "@/hooks/useValidatedDates";
interface FilterAvailableRoomProps extends CustomSearchAvailableProps {
  dataFilter?: SearchAvailableRoom | null;
  benefitOption?: { value: string; label: string }[];
  brandOption?: { value: string; label: string }[];
  onFilterChange: (f: SearchAvailableRoom | null) => void;
  SetSubmitClicked?: React.Dispatch<React.SetStateAction<boolean>>;
  filterState: SearchAvailableRoom | null;
  defaultValue?: SearchAvailableRoom | null;
  currentBrand?: HotelBrandItemNullable;
  isFirstCityChange?: React.MutableRefObject<boolean>;
  isHotelListPage?: boolean;
  isHotelOnSenPage?: boolean;
  currentTag?: SetOfTag;
  hotelSliderSections?: IHotelListSliderProps[];
  isFilterOnDestionationPage?: boolean;
}



export type SearchAvailableRoom = v.InferOutput<typeof schemaSearchAvailableRoom>;
const FilterAvailableRoom: React.FC<FilterAvailableRoomProps> = ({
  listOption,
  trigger,
  configurations,
  roomsConfigurations,
  booking,
  agesConfigurations,
  locale,
  benefitOption,
  brandOption,
  onFilterChange,
  filterState,
  defaultValue,
  currentBrand,
  isFirstCityChange,
  isHotelOnSenPage,
  currentTag,
  hotelSliderSections,
  isFilterOnDestionationPage,
  SetSubmitClicked,
  isHotelListPage,

  ...props
}) => {
  const { isOpen, toggleModal } = useModal();
  const [range, setRange] = useState<DateRange>({
    from: new Date(DEFAULT_CHECKIN_DATE),
    to: new Date(DEFAULT_CHECKOUT_DATE),
  });
  const [isDateLoading] = useState(false);

  const [previousRange, setPreviousRange] = useState<DateRange>({
    from: new Date(DEFAULT_CHECKIN_DATE),
    to: new Date(DEFAULT_CHECKOUT_DATE),
  });
  const targetLanguage = LOCALE_API_MAP[locale];
  const { setFormData, formData } = useSearchFormStore();
  const { japanAreas } = useJapanAreasStore();

  const { checkIn: validatedCheckIn, checkOut: validatedCheckOut } = useValidatedDates(
    formData?.checkIn,
    formData?.checkout
  );

  const { data } = useGetMapTextDatasourceQuery();
  const { filter_modal } = data?.homeDatasource?.map_text_datasource || {};
  const default_adult_count = roomsConfigurations?.default_adult_count;

  // Store the true initial state on component mount (doesn't update with formData changes)
  const firstStateFormData = React.useMemo(
    () => {
      const checkInInput = defaultValue?.checkIn
        ? formatDateForStorage(defaultValue.checkIn, DEFAULT_CHECKIN_DATE)
        : filterState?.checkIn
          ? formatDateForStorage(filterState.checkIn, DEFAULT_CHECKIN_DATE)
          : undefined;
      const checkOutInput = defaultValue?.checkout
        ? formatDateForStorage(defaultValue.checkout, DEFAULT_CHECKOUT_DATE)
        : filterState?.checkout
          ? formatDateForStorage(filterState.checkout, DEFAULT_CHECKOUT_DATE)
          : undefined;
      const validatedInitialDates = checkAndValidateStoredDates(
        checkInInput,
        DEFAULT_CHECKIN_DATE,
        checkOutInput,
        DEFAULT_CHECKOUT_DATE,
      );
      return ({
        hotelOrCity: findOption(
          listOption,
          defaultValue?.hotelOrCity?.value || filterState?.hotelOrCity?.value,
        ),
        checkIn: validatedInitialDates.checkIn,
        checkout: validatedInitialDates.checkOut,
        adults: defaultValue?.rooms?.[0]?.adults || default_adult_count || DEFAULT_ADULTS,
        promoCode: defaultValue?.promoCode || "",
        rooms: defaultValue?.rooms?.length
          ? defaultValue.rooms
          : [
            {
              adults: Number(default_adult_count) || Number(DEFAULT_ADULTS),
              children: [],
            },
          ],
        preferences: [],
        brands: [],
      }) as SearchAvailableRoom;
    },
    [
      listOption,
      defaultValue?.hotelOrCity?.value,
      defaultValue?.checkIn,
      defaultValue?.checkout,
      defaultValue?.rooms,
      defaultValue?.promoCode,
      filterState?.hotelOrCity?.value,
      filterState?.checkIn,
      filterState?.checkout,
      default_adult_count,
    ],
  );

  const initialFormData = useMemo(
    () => {
      return ({
        hotelOrCity: findOption(
          listOption,
          filterState?.hotelOrCity?.value || formData?.hotelOrCity?.value,
        ),
        checkIn: validatedCheckIn,
        checkout: validatedCheckOut,
        adults: formData?.rooms?.[0]?.adults || default_adult_count || DEFAULT_ADULTS,
        promoCode: formData?.promoCode || "",
        rooms: formData?.rooms?.length
          ? formData?.rooms
          : [
            {
              adults: Number(default_adult_count) || Number(DEFAULT_ADULTS),
              children: [],
            },
          ],
        preferences: filterState?.preferences ?? [],
        brands: filterState?.brands ?? [],
      }) as SearchAvailableRoom;
    },
    [formData, filterState, listOption, validatedCheckIn, validatedCheckOut, default_adult_count],
  );

  const methods = useForm<SearchAvailableRoom>({
    defaultValues: initialFormData,
    resolver: valibotResolver(schemaSearchAvailableRoom),
  });
  const onClose = () => {
    reset({ ...initialFormData });
    toggleModal();
  };
  const { reset, handleSubmit, control, formState, watch, setValue } = methods;
  const rooms = watch("rooms");
  const handleRangeSelect = (
    nextRange: DateRange | undefined,
    selectedDay: Date | undefined,
  ) => {
    // If a full range is already selected, clicking any date starts a new selection
    if (range?.from && range?.to && selectedDay) {
      setPreviousRange({ from: range.from, to: range.to });
      setRange({ from: selectedDay, to: undefined });
      setValue("checkIn", selectedDay);
      setValue("checkout", null);
      return;
    }

    // Only persist to form when both from and to are selected
    if (nextRange?.from && nextRange?.to) {
      setValue("checkIn", nextRange.from);
      setValue("checkout", nextRange.to);
      setRange(nextRange);
      setFormData({
        ...(formData as SearchAvailableRoom),
        checkIn: formatDateForStorage(nextRange.from, DEFAULT_CHECKIN_DATE),
        checkout: formatDateForStorage(nextRange.to, DEFAULT_CHECKOUT_DATE),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);
    } else {
      setRange(nextRange || { from: undefined, to: undefined });
    }
  };
  React.useEffect(() => {
    setRange({
      from: validatedCheckIn,
      to: validatedCheckOut,
    });
  }, [validatedCheckIn, validatedCheckOut]);
  const hotelOrCity = watch("hotelOrCity");

  const getFilteredBrandOptions = () => {
    if (
      !hotelOrCity?.value ||
      hotelOrCity?.type !== TypeSearch.CITY ||
      !brandOption ||
      !japanAreas
    ) {
      return brandOption ?? [];
    }

    const selectedArea = japanAreas.find(
      (area) =>
        area.documentId === hotelOrCity.value ||
        area.area_name === hotelOrCity.label ||
        area.item_name === hotelOrCity.label ||
        area.title === hotelOrCity.label,
    );

    if (!selectedArea?.hotels) {
      return brandOption ?? [];
    }

    const availableBrands = new Set<string>();

    for (const hotel of selectedArea.hotels) {
      if (!hotel) continue;

      const hasHotSpringAmenity =
        hotel.amenities_and_services?.amenities_and_services_customs?.some(
          (data: { documentId: string }) =>
            data.documentId === HOT_SPRING_AMENITY,
        );

      const matchedHotel = hotelSliderSections
        ?.flatMap((item) => item.data)
        .find((h) => h.documentId === hotel.documentId);

      const hasTravelGoalTag =
        matchedHotel?.currentTag?.documentId === currentTag?.documentId;

      if (
        (isHotelOnSenPage && !hasHotSpringAmenity) ||
        (!!currentTag && !hasTravelGoalTag)
      ) {
        continue;
      }

      const hotelBrandDocumentId = hotel.properties?.brand?.[0]?.documentId;

      if (hotelBrandDocumentId) {
        const matchingBrand = brandOption?.find(
          (brand) => brand.value === hotelBrandDocumentId,
        );

        if (matchingBrand) {
          availableBrands.add(matchingBrand.value);
        }
      }
    }

    return (
      brandOption?.filter((brand) => availableBrands.has(brand.value)) ?? []
    );
  };

  const filteredBrandOptions = getFilteredBrandOptions();

  const handleTriplaBooking = async (values: SearchAvailableRoom) => {
    const response = await fetch('/api/tripla/access-token');


    const tokenData = await response?.json() ?? {};
    const oneTimeToken = tokenData?.one_time_access_token ?? '';
    const queryParams = createBookingParams(values, {
      type: ERoomType.ROOMS,
      is_day_use: false,
      order: ERoomOrderBy.RECOMMENDED,
      is_including_occupied: false,
      locale: LOCALE_BASE_MAP[locale],
      ...(oneTimeToken && { one_time_access_token: oneTimeToken }),
    });

    if (!queryParams?.code?.length) return;

    if (isFilterOnDestionationPage) {
      onFilterChange?.({ ...values });
    }

    redirectBookingCard(
      {
        ...queryParams,
        checkin: range.from
          ? formatDateToYYYYMMDD(range.from)
          : queryParams.checkin || DEFAULT_CHECKIN_DATE,
        checkout: range.to
          ? formatDateToYYYYMMDD(range.to)
          : queryParams.checkout || DEFAULT_CHECKOUT_DATE,
        children: queryParams.children,
        kids_ages: queryParams.kids_ages,
      },
      !isFilterOnDestionationPage,
    );
  };

  const handleCityFilter = (values: SearchAvailableRoom) => {
    onFilterChange?.({
      ...values,
      brands: values.brands?.length
        ? values.brands.filter((item) =>
          filteredBrandOptions.some((option) => option.value === item.value),
        )
        : [],
      preferences: values.preferences?.length
        ? values.preferences.filter((item) =>
          benefitOption?.some((option) => option.value === item.value),
        )
        : [],
    });

    if (isFilterOnDestionationPage && values.hotelOrCity?.slug) {
      const slug_hotel =
        (japanAreas || []).find(
          (item) =>
            item?.area_name?.toLowerCase() ===
            values.hotelOrCity?.label.toLowerCase(),
        )?.slug_hotel || "";
      const cityListRoute = `${globalThis.location.origin}/${locale}/${slug_hotel}`;
      window.open(cityListRoute, "_blank");
    }
  };

  const onSubmit: SubmitHandler<SearchAvailableRoom> = (values) => {
    if (
      TRIPLA_BOOKING_URL?.length &&
      values.hotelOrCity?.type === TypeSearch.HOTEL
    ) {
      if (values.hotelOrCity?.useTripla) {
        handleTriplaBooking(values);
      } else {
        window.open(
          values.hotelOrCity?.slug || values.hotelOrCity?.externalUrl,
        );
      }

      setFormData({
        ...(formData as SearchAvailableRoom),
        hotelOrCity: values.hotelOrCity as IHotelOrCity,
      });
    } else if (values.hotelOrCity?.type === TypeSearch.CITY) {
      handleCityFilter(values);
    } else if (!values.hotelOrCity) {
      onFilterChange?.(null);
    }

    if (!isFilterOnDestionationPage) onClose();
  };

  const handleResetData = () => {
    reset({ ...firstStateFormData });
    setFormData({ ...firstStateFormData });
    setRange({
      from: firstStateFormData.checkIn || new Date(DEFAULT_CHECKIN_DATE),
      to: firstStateFormData.checkout || new Date(DEFAULT_CHECKOUT_DATE),
    });
    // Clear any applied filters
    onFilterChange?.({
      ...firstStateFormData,
      preferences: [],
      brands: [],
    });
  };

  return (
    <div {...props}>
      <Modal
        open={isOpen}
        toggleModal={onClose}
        containerClassName="rounded-2xl !max-w-[343px] md:!max-w-[643px] lg:!max-w-[743px] gap-0 max-md:!max-h-[90dvh]"
        closeClassName="bg-[var(--fill-base-main)] text-white rounded-full opacity-100 p-1.5"
        header={
          <div className="border-b border-b-[var(--stroke-gray-light)] px-4 pt-4 pb-3 text-left text-xl leading-8 font-[var(--font-semibold)] text-[var(--stroke-base-primary)]">
            {filter_modal?.filter_modal_title}
          </div>
        }
        footer={<></>}
        trigger={
          trigger ?? (
            <Button variant="secondary" size="lg">
              {booking?.bookingButtonText}
            </Button>
          )
        }
      >
        <div className="w-full overflow-hidden">
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="max-h-[60svh] overflow-y-auto px-4 pt-3 pb-4 md:max-h-[68vh]">
                <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                  {/* Hotel / City Selection */}
                  <div className="col-span-1">
                    <Label className="mb-1.5 font-[var(--font-medium)] text-[var(--text-gray-normal)]">
                      エリア名／ホテル名
                    </Label>
                    <Controller
                      name="hotelOrCity"
                      control={control}
                      render={({ field }) => (
                        <CustomSelect
                          value={field.value}
                          options={listOption}
                          filterOption={(option, inputValue) => {
                            return cityAndHotelFuzzyFilter(
                              {
                                label: option.label,
                                data: option.data as UtilSelectOption,
                              },
                              inputValue,
                            );
                          }}
                          onChange={(data, actionMeta) => {
                            if (actionMeta.action === "clear") {
                              // Reset adult count on clear
                              const userHasSavedRooms = !!formData?.rooms && formData.rooms.length > 0;
                              if (userHasSavedRooms) {
                                setValue("rooms", formData!.rooms as TRoom[]);
                              } else {
                                const configuredCount =
                                  Number(default_adult_count) || Number(DEFAULT_ADULTS);
                                const currentRooms = deepCloneRoom([...(rooms as TRoom[])]);
                                if (currentRooms[0]) {
                                  currentRooms[0] = { ...currentRooms[0], adults: configuredCount };
                                }
                                setValue("rooms", currentRooms);
                              }
                            }
                            if (actionMeta.action === "clear" && defaultValue) {
                              if (currentBrand) {
                                setValue("hotelOrCity", null);
                              } else {
                                setValue(
                                  "hotelOrCity",
                                  defaultValue.hotelOrCity,
                                );
                              }
                            } else {
                              field.onChange(data);
                              if (
                                isFirstCityChange &&
                                !isFirstCityChange.current
                              ) {
                                isFirstCityChange.current = true;
                              }
                              // Priority order for adult count on hotel/city change:
                              // 1. formData.rooms exists (user previously changed) → keep their count
                              // 2. data.initial_adult_count (per-hotel CMS field)
                              // 3. default_adult_count (global CMS config from roomsConfigurations prop)
                              // 4. DEFAULT_ADULTS (hardcoded fallback)
                              const userHasSavedRooms = !!formData?.rooms && formData.rooms.length > 0;
                              const selectedOption = data as UtilSelectOption;
                              if (userHasSavedRooms) {
                                setValue("rooms", formData!.rooms as TRoom[]);
                              } else if (selectedOption?.type === "hotel") {
                                const configuredCount =
                                  Number(selectedOption?.initial_adult_count) ||
                                  Number(default_adult_count) ||
                                  Number(DEFAULT_ADULTS);
                                const currentRooms = deepCloneRoom([...(rooms as TRoom[])]);
                                if (currentRooms[0]) {
                                  currentRooms[0] = { ...currentRooms[0], adults: configuredCount };
                                }
                                setValue("rooms", currentRooms);
                              } else if (!userHasSavedRooms && selectedOption?.type === "city") {
                                const configuredCount =
                                  Number(default_adult_count) ||
                                  Number(DEFAULT_ADULTS);
                                const currentRooms = deepCloneRoom([...(rooms as TRoom[])]);
                                if (currentRooms[0]) {
                                  currentRooms[0] = { ...currentRooms[0], adults: configuredCount };
                                }
                                setValue("rooms", currentRooms);
                              }
                            }
                          }}
                          placeholder="エリア名／ホテル名を入力してください"
                          error={formState.errors.hotelOrCity?.value?.message}
                          className={`w-full ${formState.errors.hotelOrCity ? "border-red-500" : ""}`}
                          size="sm"
                          iconCustom={<Icon icon="location-pin" />}
                          styles={{
                            control: (base) => ({
                              ...base,
                              paddingLeft: "12px",
                              paddingRight: "12px",
                              cursor: "pointer",
                            }),
                            input: (base) => ({
                              ...base,
                              fontSize: "16px",
                            }),
                            placeholder: (base) => ({
                              ...base,
                              opacity: 0.6,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              fontSize: "14px",
                            }),
                            groupHeading: (base) => ({
                              ...base,
                              fontSize: "14px",
                              fontWeight: "bold",
                              color: "var(--text-gray-stronger)",
                              padding: "8px 12px",
                            }),
                          }}
                          isClearable={!isFilterOnDestionationPage}
                        />
                      )}
                    />
                  </div>
                  {/* Date Range Selection */}
                  <div className="col-span-1">
                    <Label className="mb-1.5 font-[var(--font-medium)] text-[var(--text-gray-normal)]">
                      {configurations?.checkinLabel} -{" "}
                      {configurations?.checkoutLabel}
                    </Label>
                    <DateRangePicker
                      range={range}
                      onChange={handleRangeSelect}
                      onCancel={() => {
                        setRange(previousRange);
                        setFormData({
                          ...(formData as SearchAvailableRoom),
                          checkIn: formatDateForStorage(
                            previousRange.from,
                            DEFAULT_CHECKIN_DATE,
                          ),
                          checkout: formatDateForStorage(
                            previousRange.to,
                            DEFAULT_CHECKOUT_DATE,
                          ),
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        } as any);
                      }}
                    // note:will do it later, remove comment when client approve this feature.
                    >
                      <Button
                        hierarchy="outline"
                        variant="neutral"
                        className="flex w-full items-center justify-between rounded-md border border-gray-300 px-3 py-4 text-sm font-medium text-[var(--text-gray-light)]"
                      >
                        <span>
                          {range?.from
                            ? format(range.from, datePickerFormatJP)
                            : configurations?.checkinLabel}
                        </span>
                        <ArrowRight
                          className="text-[var(--text-gray-light)]"
                          width={16}
                          height={16}
                        />
                        <span>
                          {range?.to
                            ? format(range.to, datePickerFormatJP)
                            : configurations?.checkoutLabel}
                        </span>
                        {isDateLoading ? (
                          <Loading />
                        ) : (
                          <Icon icon={"calendar"} />
                        )}
                      </Button>
                    </DateRangePicker>
                  </div>
                  {/* Room Select */}
                  <div className="col-span-1">
                    <RoomSelect
                      roomConfiguration={roomsConfigurations}
                      AgesConfigurations={agesConfigurations}
                    />
                  </div>

                  {/* Promo Code Input */}
                  <div className="col-span-1">
                    <Label className="font-medium text-[var(--text-gray-normal)]">
                      {configurations?.promoCodeLabel}
                    </Label>
                    <Controller
                      name="promoCode"
                      control={control}
                      render={({ field }) => (
                        <Input
                          className="text-md mt-auto max-h-[43px] w-full rounded-md border border-gray-300 bg-white py-[12px] text-[var(--text-gray-normal)] placeholder:text-sm"
                          value={field.value ?? ""}
                          onChange={(e) => {
                            field.onChange(e);
                            setFormData({
                              ...(formData as SearchAvailableRoom),
                              promoCode: e.target.value,
                            });
                          }}
                          placeholder={
                            targetLanguage === "ja"
                              ? "プロモコードを入力してください"
                              : configurations?.promoCodePlaceholder
                          }
                          size="sm"
                        />
                      )}
                    />
                  </div>
                  {hotelOrCity?.type &&
                    hotelOrCity?.type === TypeSearch.CITY &&
                    !isFilterOnDestionationPage && (
                      <div className="col-span-1 xl:col-span-2">
                        <div className={`transition-all duration-300`}>
                          <div className="grid w-full grid-cols-1 md:grid-cols-2">
                            {!currentBrand && (
                              <Controller
                                name="brands"
                                control={control}
                                render={({ field }) => (
                                  <CheckboxGroup
                                    className="col-span-1 mt-4"
                                    title={
                                      filter_modal?.subtitle_brand ||
                                      "ブランドの絞り込み"
                                    }
                                    options={filteredBrandOptions}
                                    selectedOptions={field.value}
                                    onChange={(newSelectedBrands) =>
                                      field.onChange(newSelectedBrands)
                                    }
                                  />
                                )}
                              />
                            )}
                            <Controller
                              name="preferences"
                              control={control}
                              render={({ field }) => (
                                <CheckboxGroup
                                  className="col-span-1 mt-4"
                                  title={
                                    filter_modal?.subtitle_conditions ||
                                    "条件の絞り込み"
                                  }
                                  options={benefitOption ?? []}
                                  selectedOptions={field.value}
                                  onChange={(newSelectedPreferences) =>
                                    field.onChange(newSelectedPreferences)
                                  }
                                />
                              )}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                </div>
              </div>
              {/* Form Footer */}
              <hr className="text-[var(--stroke-gray-light)]" />
              <div
                className={`mx-auto grid w-full grid-cols-2 gap-4 px-4 pt-3 pb-4 md:w-[80%]`}
              >
                <button
                  type="button"
                  className="col-span-1 flex w-full cursor-pointer items-center justify-center rounded-md bg-[var(--fill-base-gray)] px-4 text-sm text-[var(--text-gray-normal)] md:text-base"
                  onClick={(event) => {
                    event.preventDefault();
                    handleResetData();
                  }}
                >
                  {filter_modal?.button_reset_text || "リセット"}
                </button>

                <Button
                  variant="secondary"
                  className="col-span-1 flex w-full items-center justify-center text-center"
                  size="lg"
                  type="submit"
                >
                  <span>{filter_modal?.button_search_text || "検索"}</span>
                </Button>
              </div>
            </form>
          </FormProvider>
        </div>
      </Modal>
    </div>
  );
};

export { FilterAvailableRoom };
